<template>
    <div class="choices-select-wrapper" :class="{ 'is-invalid': errorMessage }" v-bind="$attrs">
        <select ref="selectEl" v-model="localValue" class="form-control">
            <option :value="''" selected>{{ placeholder }}</option>
            <option v-for="opt in normalizedOptions" :key="opt.id" :value="opt.id">
                {{ opt.text }}
            </option>
        </select>
    </div>
    <div v-if="errorMessage" class="invalid-feedback">{{ errorMessage }}</div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, nextTick, watch, computed } from 'vue';
import Choices from 'choices.js';
import 'choices.js/public/assets/styles/choices.min.css';

const props = defineProps({
    modelValue: { type: [String, Number], default: '' },
    options: {
        type: Array,
        required: true
    },
    placeholder: {
        type: String,
        default: 'Seleccionar'
    },
    errors: { type: Object, default: () => ({}) },
    errorFieldName: { type: String, default: '' }
});
const emit = defineEmits(['update:modelValue']);

const selectEl = ref(null);
const choicesInstance = ref(null);

const normalizedOptions = computed(() => {
    return (props.options || []).map((o) => {
        if (typeof o === 'string') return { id: o, text: o };
        if (typeof o === 'number') return { id: String(o), text: String(o) };
        return { id: o.id ?? '', text: o.text ?? String(o.id ?? '') };
    });
});

const errorMessage = computed(() => props.errors?.[props.errorFieldName]?.[0] || null);

const localValue = computed({
    get() {
        return props.modelValue;
    },
    set(v) {
        emit('update:modelValue', v);
        if (choicesInstance.value) {
            choicesInstance.value.setChoiceByValue(v || '');
        }
    }
});

// Resalta la opción actualmente seleccionada cuando se abre el dropdown
const highlightSelectedOption = () => {
    if (!choicesInstance.value || !props.modelValue) return;
    const selectedVal = String(props.modelValue);
    const el = document.querySelector(`.choices__list--dropdown .choices__item[data-value="${selectedVal}"]`);
    if (el) {
        choicesInstance.value._highlightChoice(el);
    }
};

// Función para posicionar el dropdown automáticamente
const positionDropdown = () => {
    if (!selectEl.value) return;

    const choicesWrapper = selectEl.value.closest('.choices-select-wrapper');
    if (!choicesWrapper) return;

    const choicesContainer = choicesWrapper.querySelector('.choices');
    if (!choicesContainer) return;

    const rect = choicesContainer.getBoundingClientRect();
    const dropdownHeight = 280; // max-height del dropdown
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;

    // Si no hay suficiente espacio abajo pero sí arriba, posicionar hacia arriba
    if (spaceBelow < dropdownHeight && spaceAbove > dropdownHeight) {
        choicesWrapper.classList.add('choices-dropup');
    } else {
        choicesWrapper.classList.remove('choices-dropup');
    }
};

// Función para actualizar el valor seleccionado en Choices.js
const updateSelectedValue = () => {
    if (choicesInstance.value) {
        choicesInstance.value.setChoiceByValue(props.modelValue || '');
    }
};

const initChoices = async () => {
    await nextTick();
    destroyChoices();
    if (!selectEl.value) return;
    choicesInstance.value = new Choices(selectEl.value, {
        searchEnabled: false,
        allowHTML: true,
        placeholder: true,
        itemSelectText: '',
        shouldSort: false
    });
    selectEl.value.addEventListener('change', (e) => {
        emit('update:modelValue', e.target.value);
    });
    // Escuchar apertura para resaltar la opción seleccionada y posicionar el dropdown
    selectEl.value.addEventListener('showDropdown', () => {
        highlightSelectedOption();
        setTimeout(positionDropdown, 10); // Pequeño delay para asegurar que el DOM esté actualizado
    });
    // set initial
    updateSelectedValue();
};

const destroyChoices = () => {
    if (choicesInstance.value) {
        choicesInstance.value.destroy();
        choicesInstance.value = null;
    }
};

onMounted(() => {
    initChoices();
});

onBeforeUnmount(() => {
    destroyChoices();
});

// Vuelve a inicializar Choices.js si `modelValue` cambia
watch(() => props.modelValue, () => {
    updateSelectedValue();
});

watch(normalizedOptions, () => {
    initChoices();
});
</script>

<style>
.is-invalid {
    border: 0px solid red;
    border-radius: 10px;
}

.is-invalid .choices__inner {
    border: 1px solid red !important;
}

/* Quitar flecha nativa y apariencia del <select> original */
.choices-select-wrapper select.form-control {
    -webkit-appearance: none;
    -moz-appearance: none;
    appearance: none;
    background-image: none !important;
}

/* Ancho y layout del contenedor de Choices */
.choices-select-wrapper {
    overflow: visible !important;
}

.choices-select-wrapper .choices {
    width: 100%;
    margin-bottom: 0;
    overflow: visible !important;
}

/* Input visual - diseño moderno y sutil */
.choices-select-wrapper .choices__inner {
    min-height: 44px;
    height: 44px;
    display: flex;
    align-items: center;
    border-radius: 12px;
    border: 1.5px solid #e2e8f0;
    background: #ffffff;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
    padding: 6px 40px 6px 14px;
    transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
    font-size: 0.9375rem;
}

.choices-select-wrapper .choices__inner:hover {
    border-color: #cbd5e1;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

/* Flecha sutil y moderna */
.choices-select-wrapper .choices[data-type*="select-one"]::after {
    border: none;
    content: '';
    position: absolute;
    right: 16px;
    top: 50%;
    width: 0;
    height: 0;
    margin-top: -2px;
    border-left: 4px solid transparent;
    border-right: 4px solid transparent;
    border-top: 5px solid #94a3b8;
    pointer-events: none;
    transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
}

.choices-select-wrapper .choices[data-type*="select-one"].is-open::after {
    border-top: none;
    border-bottom: 5px solid #667eea;
    margin-top: -3px;
    transform: rotate(0deg);
}

/* Dropdown moderno y sutil - oculto por defecto */
.choices-select-wrapper .choices__list--dropdown {
    display: none !important;
}

.choices-select-wrapper .is-open .choices__list--dropdown,
.choices-select-wrapper .choices__list--dropdown.is-active {
    display: block !important;
    position: absolute !important;
    top: 100% !important;
    left: 0 !important;
    right: 0 !important;
    margin: -1.5px 0 0 0 !important;
    padding: 0 !important;
    border: 1.5px solid #e2e8f0 !important;
    border-top: 1.5px solid transparent !important;
    border-top-left-radius: 0 !important;
    border-top-right-radius: 0 !important;
    border-bottom-left-radius: 12px !important;
    border-bottom-right-radius: 12px !important;
    background: #ffffff !important;
    z-index: 10060 !important;
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.08), 0 4px 10px rgba(0, 0, 0, 0.04) !important;
    transform: none !important;
    max-height: 280px !important;
    overflow: hidden !important;
    visibility: visible !important;
    pointer-events: auto !important;
    opacity: 1 !important;
}

/* Contenedor de scroll interno */
.choices-select-wrapper .choices__list--dropdown .choices__list {
    max-height: 280px !important;
    overflow-y: auto !important;
    overflow-x: hidden !important;
    padding: 4px 0 !important;
}

/* Posicionamiento hacia arriba cuando no hay espacio abajo */
.choices-select-wrapper.choices-dropup .is-open .choices__list--dropdown,
.choices-select-wrapper.choices-dropup .choices__list--dropdown.is-active {
    top: auto !important;
    bottom: 100% !important;
    margin: 0 0 -1.5px 0 !important;
    border-top: 1.5px solid #e2e8f0 !important;
    border-bottom: 1.5px solid transparent !important;
    border-top-left-radius: 12px !important;
    border-top-right-radius: 12px !important;
    border-bottom-left-radius: 0 !important;
    border-bottom-right-radius: 0 !important;
}

/* Ajustar el input cuando está en modo dropup */
.choices-select-wrapper.choices-dropup .choices.is-open .choices__inner {
    border-bottom-left-radius: 12px !important;
    border-bottom-right-radius: 12px !important;
    border-top-left-radius: 0 !important;
    border-top-right-radius: 0 !important;
    border-top-color: transparent !important;
}

/* Elimina cualquier separación o elemento decorativo */
.choices-select-wrapper .choices__list--dropdown::before,
.choices-select-wrapper .choices__list--dropdown::after {
    display: none !important;
}

/* Estado abierto - integración perfecta sin separación */
.choices-select-wrapper .is-open .choices__inner {
    border-bottom-left-radius: 0 !important;
    border-bottom-right-radius: 0 !important;
    border-bottom: 1.5px solid transparent !important;
    border-color: #e2e8f0;
    box-shadow: 0 0 0 1px rgba(102, 126, 234, 0.1);
    background: #fafbfc;
}

/* Evita flip y cualquier cambio de posición */
.choices-select-wrapper .is-flipped .choices__list--dropdown {
    bottom: auto !important;
    top: 100% !important;
    margin: -1.5px 0 0 0 !important;
    transform: none !important;
}

/* Oculta el placeholder vacío */
.choices-select-wrapper .choices__list--dropdown .choices__item[data-value=""],
.choices-select-wrapper .choices__list--dropdown .choices__placeholder {
    display: none !important;
}

/* Scrollbar moderno y elegante con gradiente */
.choices-select-wrapper .choices__list--dropdown .choices__list::-webkit-scrollbar {
    width: 7px;
}

.choices-select-wrapper .choices__list--dropdown .choices__list::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 10px;
    margin: 4px 0;
    border: 1px solid rgba(226, 232, 240, 0.5);
}

.choices-select-wrapper .choices__list--dropdown .choices__list::-webkit-scrollbar-thumb {
    background: linear-gradient(180deg, #6366f1 0%, #8b5cf6 100%);
    border-radius: 10px;
    border: 2px solid #f1f5f9;
    transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
    box-shadow: 0 2px 4px rgba(99, 102, 241, 0.2);
}

.choices-select-wrapper .choices__list--dropdown .choices__list::-webkit-scrollbar-thumb:hover {
    background: linear-gradient(180deg, #4f46e5 0%, #7c3aed 100%);
    border-color: #e2e8f0;
    transform: scaleX(1.3);
    box-shadow: 0 3px 8px rgba(99, 102, 241, 0.35);
}

.choices-select-wrapper .choices__list--dropdown .choices__list::-webkit-scrollbar-thumb:active {
    background: linear-gradient(180deg, #4338ca 0%, #6b21a8 100%);
}

/* Items - diseño sutil y minimalista */
.choices-select-wrapper .choices__list--dropdown .choices__item {
    padding: 10px 16px;
    line-height: 1.5;
    margin: 0;
    font-size: 0.9375rem;
    color: #475569;
    transition: all 0.2s ease;
    cursor: pointer;
    border-left: 2px solid transparent;
    background: transparent;
}

/* Hover muy sutil */
.choices-select-wrapper .choices__list--dropdown .choices__item:hover,
.choices-select-wrapper .choices__list--dropdown .choices__item.is-highlighted {
    background: #f8fafc;
    color: #1e293b;
}

/* Item seleccionado con indicador minimalista */
.choices-select-wrapper .choices__list--dropdown .choices__item.is-selected {
    background: #f1f5f9;
    color: #475569;
    font-weight: 500;
    position: relative;
}

/* Checkmark sutil para item seleccionado */
.choices-select-wrapper .choices__list--dropdown .choices__item.is-selected::after {
    content: '✓';
    position: absolute;
    right: 16px;
    top: 50%;
    transform: translateY(-50%);
    color: #64748b;
    font-weight: 600;
    font-size: 0.875rem;
}

/* Espaciado para primera y última opción */
.choices-select-wrapper .choices__list--dropdown .choices__item:first-child {
    margin-top: 2px;
}

.choices-select-wrapper .choices__list--dropdown .choices__item:last-child {
    margin-bottom: 2px;
}

/* Animación suave y sutil al abrir */
.choices-select-wrapper .choices__list--dropdown.is-active {
    animation: fadeInDropdown 0.2s ease-out;
}

@keyframes fadeInDropdown {
    from {
        opacity: 0;
        transform: translateY(-4px);
    }

    to {
        opacity: 1;
        transform: translateY(0);
    }
}

.choices-select-wrapper .choices__list--single .choices__item {
    font-size: 0.9375rem;
    color: #1e293b;
    font-weight: 500;
}

/* Texto del placeholder más sutil */
.choices-select-wrapper .choices__list--single .choices__placeholder {
    opacity: 0.5;
    color: #94a3b8;
}
</style>
